import { useState, useEffect, useCallback } from 'react';

export type PermType = 'camera' | 'geolocation';
export type PermissionValue = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown';

export interface PermissionStates {
    camera: PermissionValue;
    geolocation: PermissionValue;
}

/** Detect if running as iOS standalone PWA */
const isIosPwa = (): boolean => {
    if (typeof window === 'undefined') return false;
    const nav = window.navigator as any;
    const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent);
    return isIos && (nav.standalone === true);
};

/** Detect iOS Safari (not PWA) */
const isIosSafari = (): boolean => {
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
};

const usePermissions = () => {
    const [permissions, setPermissions] = useState<PermissionStates>({
        camera: 'unknown',
        geolocation: 'unknown'
    });

    const queryPermission = useCallback(async (name: PermType): Promise<PermissionValue> => {
        if (!navigator.permissions) return 'unsupported';

        try {
            const result = await navigator.permissions.query({ name: name as globalThis.PermissionName });
            return result.state as PermissionValue;
        } catch {
            // iOS Safari doesn't support querying 'camera' permission
            return 'unsupported';
        }
    }, []);

    const checkAll = useCallback(async () => {
        const [camera, geolocation] = await Promise.all([
            queryPermission('camera'),
            queryPermission('geolocation')
        ]);
        setPermissions({ camera, geolocation });
    }, [queryPermission]);

    // Listen for permission changes
    useEffect(() => {
        checkAll();

        const listeners: Array<() => void> = [];

        const listen = async (name: PermType) => {
            if (!navigator.permissions) return;
            try {
                const status = await navigator.permissions.query({ name: name as globalThis.PermissionName });
                const handler = () => {
                    setPermissions(prev => ({ ...prev, [name]: status.state as PermissionValue }));
                };
                status.addEventListener('change', handler);
                listeners.push(() => status.removeEventListener('change', handler));
            } catch {
                // Permission not queryable on this browser
            }
        };

        listen('camera');
        listen('geolocation');

        return () => listeners.forEach(cleanup => cleanup());
    }, [checkAll]);

    const getGuidanceText = useCallback((name: PermType, state: PermissionValue): string => {
        if (state === 'granted') return 'Access granted';
        if (state === 'prompt') return 'Will be requested when needed';
        if (state === 'unsupported') {
            if (isIosPwa()) return 'On iOS, permissions may reset when the app restarts. Allow access when prompted.';
            return 'Cannot check permission status on this browser';
        }
        if (state === 'denied') {
            if (isIosSafari() || isIosPwa()) {
                return name === 'camera'
                    ? 'Denied — go to iOS Settings > Safari > Camera to re-enable'
                    : 'Denied — go to iOS Settings > Safari > Location to re-enable';
            }
            return name === 'camera'
                ? 'Denied — click the lock icon in the address bar to re-enable camera access'
                : 'Denied — click the lock icon in the address bar to re-enable location access';
        }
        return '';
    }, []);

    return {
        permissions,
        checkAll,
        isIosPwa: isIosPwa(),
        isIosSafari: isIosSafari(),
        getGuidanceText
    };
};

export default usePermissions;
